import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useData } from "@/hooks/useData";
import { Company } from "@/types/company/company.types";
import { Stepper, StepperControls, StepType } from "@/components/ui/stepper";
import { YearSelectionStep } from "@/components/footprints/stepper/steps/YearSelectionStep";
import { AnnualConsumptionsStep } from "@/components/footprints/stepper/steps/AnnualConsumptionsStep";
import { GeneralDataStep } from "@/components/footprints/stepper/steps/GeneralDataStep";
import { ProductStep } from "@/components/footprints/stepper/steps/ProductStep";
import { RevisionSubmissionStep } from "@/components/footprints/stepper/steps/RevisionSubmissionStep";
import { CalculationInputs, ProductPayload, ProductEntry } from "@/types/product/productStep.types";
import { loadFootprintsData, saveFootprintsAsDraft } from "../services/footprintsService";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { validateProductTotals } from "@/components/footprints/utils/calculations/extraction";
import { FuelSourceDocWithId } from "@/types/fuel";
import {
  calculateFuelCarbonFootprint,
  calculateFuelHydricFootprint,
} from "@/components/footprints/utils/calculations/annualConsumptions";

/**
 * Calculate product validation flags for loaded data
 */
const calculateProductValidation = (products: any, generalData: any, activityType?: string) => {
  if (!products?.entries?.length || !generalData) {
    return { ...products, isProductionValid: false, isRevenueValid: false };
  }

  const isMixedCompany = activityType === "extraction_and_transformation";
  const isTransformationCompany = activityType === "transformation";
  const isExtractionCompany = activityType === "extraction";

  let isProductionValid = false;
  let isRevenueValid = false;

  if (isMixedCompany) {
    // For mixed companies, validate each activity type separately
    const extractionProducts = products.entries.filter((p: any) => p.productFlow === "extraction");
    const transformationProducts = products.entries.filter((p: any) => p.productFlow === "transformation");

    // Calculate extraction totals
    const extractionValidation = validateProductTotals(extractionProducts, {
      prod_ext: generalData.prod_ext || 0,
      fat_ext: generalData.fat_ext || 0,
    });

    // Calculate transformation totals
    const transformationValidation = validateProductTotals(transformationProducts, {
      prod_ext: generalData.prod_trsf || 0,
      fat_ext: generalData.fat_trsf || 0,
    });

    isProductionValid = extractionValidation.isProductionValid && transformationValidation.isProductionValid;
    isRevenueValid = extractionValidation.isRevenueValid && transformationValidation.isRevenueValid;
  } else {
    // For single activity companies
    let targetData;
    if (isTransformationCompany) {
      targetData = {
        prod_ext: generalData.prod_trsf || 0,
        fat_ext: generalData.fat_trsf || 0,
      };
    } else if (isExtractionCompany) {
      targetData = {
        prod_ext: generalData.prod_ext || 0,
        fat_ext: generalData.fat_ext || 0,
      };
    } else {
      // Fallback
      targetData = {
        prod_ext: generalData.prod_ext || 0,
        fat_ext: generalData.fat_ext || 0,
      };
    }

    const validation = validateProductTotals(products.entries, targetData);
    isProductionValid = validation.isProductionValid;
    isRevenueValid = validation.isRevenueValid;
  }

  return {
    ...products,
    isProductionValid,
    isRevenueValid,
  };
};

export const Footprints = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const { toast } = useToast();

  // Fetch company data to get activity type
  const { document: company } = useData<Company>({
    collectionName: "companies",
    documentId: claims?.companyId,
  });

  // Fetch fuel sources to recompute fuel totals with current emission factors
  const { data: fuelSources } = useData<FuelSourceDocWithId>({
    collectionName: "setup_fuels",
  });

  // Core state
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [subStepIndex, setSubStepIndex] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});

  // Data loading state
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [_originalStatus, setOriginalStatus] = useState<"draft" | "submitted" | null>(null);

  // Save state
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Record<string, any>>({});

  // Ref to prevent duplicate loading
  const isLoadingRef = useRef(false);

  // Steps state
  const [steps, setSteps] = useState<StepType[]>([]);

  // Memoized steps configuration
  const stepsConfig: StepType[] = useMemo(
    () => [
      {
        id: "year",
        title: t("footprints.selectYear"),
        isCompleted: Boolean(selectedYear),
        isOptional: false,
      },
      {
        id: "annualConsumptions",
        title: t("footprints.consumption.title"),
        isEnabled: Boolean(selectedYear),
        isOptional: false,
      },
      {
        id: "generalData",
        title: t("footprints.generalData.title"),
        isEnabled: Boolean(selectedYear),
        isOptional: false,
      },
      {
        id: "products",
        title: t("products.title"),
        isEnabled: Boolean(selectedYear),
        isOptional: false,
      },
      {
        id: "revisionSubmission",
        title: t("footprints.revisionSubmission.title"),
        isEnabled: Boolean(selectedYear),
        isOptional: false,
      },
    ],
    [selectedYear, t]
  );

  // Initialize steps when config changes
  useEffect(() => {
    setSteps(stepsConfig);
  }, [stepsConfig]);

  // Memoized calculation inputs
  const calculationInputs: CalculationInputs = useMemo(() => {
    const generalData = formData.generalData || {};
    const isMixed = company?.activityType === "extraction_and_transformation";

    // Recompute fuel totals from individual entries using current emission factors.
    // This corrects stale stored totals when fuel factors have changed in setup_fuels
    // since the draft was last saved — without requiring the user to visit FuelsStep.
    const recomputeFuelTotals = (entries: any[] = []) => {
      if (!fuelSources.length) return null; // not loaded yet, fall back to stored values
      let total_pc_comb = 0;
      let total_ph_comb = 0;
      for (const entry of entries) {
        if (!entry.quantity || Number(entry.quantity) === 0) continue;
        const sourceDoc = fuelSources.find((f: any) => f.name === entry.fuelSource);
        if (!sourceDoc) {
          // Source not found in setup — use stored per-entry value
          total_pc_comb += entry.pc_comb || 0;
          total_ph_comb += entry.ph_comb || 0;
          continue;
        }
        total_pc_comb += calculateFuelCarbonFootprint(entry, sourceDoc).footprint;
        total_ph_comb += calculateFuelHydricFootprint(entry, sourceDoc).footprint;
      }
      return { total_pc_comb, total_ph_comb };
    };

    // Ensure numeric fields are properly converted from strings to numbers
    const convertedGeneralData = {
      ...generalData,
      prod_ext: Number(generalData.prod_ext) || 0,
      fat_ext: Number(generalData.fat_ext) || 0,
      prod_trsf: Number(generalData.prod_trsf) || 0,
      fat_trsf: Number(generalData.fat_trsf) || 0,
      pvp_caro_ext: Number(generalData.pvp_caro_ext) || 0,
      pvp_barato_ext: Number(generalData.pvp_barato_ext) || 0,
      pvp_caro_trsf: Number(generalData.pvp_caro_trsf) || 0,
      pvp_barato_trsf: Number(generalData.pvp_barato_trsf) || 0,
      rend: Number(generalData.rend) || 0,
    };

    if (isMixed) {
      const annualConsumptions = (formData.annualConsumptions as any) || {};
      const extractionConsumptions = annualConsumptions.extraction || {};
      const transformationConsumptions = annualConsumptions.transformation || {};
      const extFuels = recomputeFuelTotals(extractionConsumptions.fuels?.entries);
      const trsfFuels = recomputeFuelTotals(transformationConsumptions.fuels?.entries);
      return {
        generalData: {
          year: parseInt(selectedYear) || new Date().getFullYear(),
          companyId: claims?.companyId || "",
          ...convertedGeneralData,
          activityType: company?.activityType,
        },
        consumptions: {
          extraction: {
            materials: {
              total_pc_mat: extractionConsumptions.materials?.total_pc_mat || 0,
              total_ph_mat: extractionConsumptions.materials?.total_ph_mat || 0,
              entries: extractionConsumptions.materials?.entries || [],
            },
            electricity: {
              total_pc_ele: extractionConsumptions.electricity?.total_pc_ele || 0,
              total_ph_ele: extractionConsumptions.electricity?.total_ph_ele || 0,
            },
            fuels: {
              total_pc_comb: extFuels?.total_pc_comb ?? extractionConsumptions.fuels?.total_pc_comb ?? 0,
              total_ph_comb: extFuels?.total_ph_comb ?? extractionConsumptions.fuels?.total_ph_comb ?? 0,
            },
            residue: {
              total_pc_res: extractionConsumptions.residue?.total_pc_res || 0,
            },
            water: {
              total_ph_agua: extractionConsumptions.water?.ph_agua || 0,
            },
          },
          transformation: {
            materials: {
              total_pc_mat: transformationConsumptions.materials?.total_pc_mat || 0,
              total_ph_mat: transformationConsumptions.materials?.total_ph_mat || 0,
              entries: transformationConsumptions.materials?.entries || [],
            },
            electricity: {
              total_pc_ele: transformationConsumptions.electricity?.total_pc_ele || 0,
              total_ph_ele: transformationConsumptions.electricity?.total_ph_ele || 0,
            },
            fuels: {
              total_pc_comb: trsfFuels?.total_pc_comb ?? transformationConsumptions.fuels?.total_pc_comb ?? 0,
              total_ph_comb: trsfFuels?.total_ph_comb ?? transformationConsumptions.fuels?.total_ph_comb ?? 0,
            },
            residue: {
              total_pc_res: transformationConsumptions.residue?.total_pc_res || 0,
            },
            water: {
              total_ph_agua: transformationConsumptions.water?.ph_agua || 0,
            },
          },
        },
      };
    }

    // Default for single-activity companies
    const singleFuels = recomputeFuelTotals(formData.fuels?.entries);
    return {
      generalData: {
        year: parseInt(selectedYear) || new Date().getFullYear(),
        companyId: claims?.companyId || "",
        ...convertedGeneralData,
        activityType: company?.activityType,
      },
      consumptions: {
        materials: {
          total_pc_mat: formData.materials?.total_pc_mat || 0,
          total_ph_mat: formData.materials?.total_ph_mat || 0,
        },
        electricity: {
          total_pc_ele: formData.electricity?.total_pc_ele || 0,
          total_ph_ele: formData.electricity?.total_ph_ele || 0,
        },
        fuels: {
          total_pc_comb: singleFuels?.total_pc_comb ?? formData.fuels?.total_pc_comb ?? 0,
          total_ph_comb: singleFuels?.total_ph_comb ?? formData.fuels?.total_ph_comb ?? 0,
        },
        residue: {
          total_pc_res: formData.residue?.total_pc_res || 0,
        },
        water: {
          total_ph_agua: formData.water?.total_ph_agua || 0,
        },
      },
    };
  }, [formData, selectedYear, claims?.companyId, company?.activityType, fuelSources]);

  /**
   * Load existing data for the selected year
   */
  const loadExistingData = useCallback(
    async (year: string, companyId: string) => {
      if (isLoadingRef.current) return;

      isLoadingRef.current = true;
      setIsLoadingData(true);
      setLoadingError(null);

      try {
        const { formData: existingFormData, status, lastUpdated } = await loadFootprintsData(year, companyId);

        if (existingFormData) {
          // Map backend structure based on company activity type
          const activityType = existingFormData.generalData?.activityType;
          const isMixed = activityType === "extraction_and_transformation";
          let mappedFormData = { ...existingFormData };

          if (isMixed) {
            // For mixed companies, check if backend already returned annualConsumptions structure
            if (existingFormData.annualConsumptions) {
              // Backend already reconstructed the correct structure, use it directly
              mappedFormData.annualConsumptions = existingFormData.annualConsumptions;
            } else {
              // Fallback: Backend returned flat structure, reconstruct it
              mappedFormData.annualConsumptions = {
                extraction: {
                  materials: existingFormData.materials?.extraction || {},
                  electricity: existingFormData.electricity?.extraction || {},
                  fuels: existingFormData.fuels?.extraction || {},
                  residue: existingFormData.residue?.extraction || {},
                  water: existingFormData.water?.extraction || {},
                },
                transformation: {
                  materials: existingFormData.materials?.transformation || {},
                  electricity: existingFormData.electricity?.transformation || {},
                  fuels: existingFormData.fuels?.transformation || {},
                  residue: existingFormData.residue?.transformation || {},
                  water: existingFormData.water?.transformation || {},
                },
              };
            }

            // Clean up original top-level keys to avoid data duplication
            delete mappedFormData.materials;
            delete mappedFormData.electricity;
            delete mappedFormData.fuels;
            delete mappedFormData.residue;
            delete mappedFormData.water;
          } else if (activityType === "extraction" || activityType === "transformation") {
            // For single-activity companies, flatten the data structure to the top level
            // First try the nested structure, then fall back to flat structure
            mappedFormData.materials = existingFormData.materials?.[activityType] || existingFormData.materials || {};
            mappedFormData.electricity =
              existingFormData.electricity?.[activityType] || existingFormData.electricity || {};
            mappedFormData.fuels = existingFormData.fuels?.[activityType] || existingFormData.fuels || {};
            mappedFormData.residue = existingFormData.residue?.[activityType] || existingFormData.residue || {};
            mappedFormData.water = existingFormData.water?.[activityType] || existingFormData.water || {};
          }
          // Calculate product validation flags for loaded data
          if (mappedFormData.products?.entries?.length > 0 && mappedFormData.generalData) {
            // Convert general data to ensure numeric values for validation
            const originalGeneralData = mappedFormData.generalData;
            const convertedGeneralData = {
              ...originalGeneralData,
              prod_ext: Number(originalGeneralData.prod_ext) || 0,
              fat_ext: Number(originalGeneralData.fat_ext) || 0,
              prod_trsf: Number(originalGeneralData.prod_trsf) || 0,
              fat_trsf: Number(originalGeneralData.fat_trsf) || 0,
              pvp_caro_ext: Number(originalGeneralData.pvp_caro_ext) || 0,
              pvp_barato_ext: Number(originalGeneralData.pvp_barato_ext) || 0,
              pvp_caro_trsf: Number(originalGeneralData.pvp_caro_trsf) || 0,
              pvp_barato_trsf: Number(originalGeneralData.pvp_barato_trsf) || 0,
              rend: Number(originalGeneralData.rend) || 0,
            };
            
            // Get activityType from the loaded data or fallback to company
            const activityType = mappedFormData.generalData?.activityType || company?.activityType;
            
            mappedFormData.products = calculateProductValidation(
              mappedFormData.products,
              convertedGeneralData,
              activityType
            );
          }

          setFormData(mappedFormData);
          setIsEditMode(true);
          setOriginalStatus(status);
          setLastSaved(lastUpdated);
          setOriginalFormData(mappedFormData);
          evaluateStepCompletionStates(mappedFormData, status);

          toast({
            title: t("footprints.dataLoadedForEditing", { year }),
          });
        } else {
          resetToCreateMode();
        }
      } catch (error) {
        console.error("Error loading existing data:", error);
        setLoadingError("Erro ao carregar dados existentes. Tente novamente.");
        resetToCreateMode();
      } finally {
        setIsLoadingData(false);
        isLoadingRef.current = false;
      }
    },
    [toast, t]
  );

  /**
   * Reset to create mode (no existing data)
   */
  const resetToCreateMode = useCallback(() => {
    setFormData({});
    setIsEditMode(false);
    setOriginalStatus(null);
    setLastSaved(null);
    setOriginalFormData({});
  }, []);

  /**
   * Evaluate step completion states based on loaded data
   */
  const evaluateStepCompletionStates = useCallback(
    (loadedFormData: Record<string, any>, status?: "draft" | "submitted" | null) => {
      // Convert general data to ensure numeric values
      const originalGeneralData = loadedFormData.generalData || {};
      const convertedGeneralData = {
        ...originalGeneralData,
        prod_ext: Number(originalGeneralData.prod_ext) || 0,
        fat_ext: Number(originalGeneralData.fat_ext) || 0,
        prod_trsf: Number(originalGeneralData.prod_trsf) || 0,
        fat_trsf: Number(originalGeneralData.fat_trsf) || 0,
        pvp_caro_ext: Number(originalGeneralData.pvp_caro_ext) || 0,
        pvp_barato_ext: Number(originalGeneralData.pvp_barato_ext) || 0,
        pvp_caro_trsf: Number(originalGeneralData.pvp_caro_trsf) || 0,
        pvp_barato_trsf: Number(originalGeneralData.pvp_barato_trsf) || 0,
        rend: Number(originalGeneralData.rend) || 0,
      };
      // Step 0: Year Selection - always completed if we have data
      const yearCompleted = true;

      // Determine company type
      const isExtractionCompany = company?.activityType === "extraction";
      const isTransformationCompany = company?.activityType === "transformation";
      const isMixedCompany = company?.activityType === "extraction_and_transformation";

      // Step 1: Annual Consumptions - require ALL sub-steps to have data (matching Review step validation)
      let hasConsumptionData = false;
      if (isMixedCompany) {
        const extraction = loadedFormData.annualConsumptions?.extraction || {};
        const transformation = loadedFormData.annualConsumptions?.transformation || {};

        // For mixed companies, require ALL categories to have data in at least one section (extraction OR transformation)
        const hasMaterials = extraction.materials?.entries?.length > 0 || transformation.materials?.entries?.length > 0;
        const hasElectricity =
          extraction.electricity?.entries?.length > 0 || transformation.electricity?.entries?.length > 0;
        const hasFuels = extraction.fuels?.entries?.length > 0 || transformation.fuels?.entries?.length > 0;
        const hasResidue = extraction.residue?.entries?.length > 0 || transformation.residue?.entries?.length > 0;
        const hasWater = extraction.water?.waterEntries?.length > 0 || transformation.water?.waterEntries?.length > 0;

        hasConsumptionData = hasMaterials && hasElectricity && hasFuels && hasResidue && hasWater;
      } else {
        // For single-activity companies, require ALL categories to have data
        const hasMaterials = loadedFormData.materials?.entries?.length > 0;
        const hasElectricity = loadedFormData.electricity?.entries?.length > 0;
        const hasFuels = loadedFormData.fuels?.entries?.length > 0;
        const hasResidue = loadedFormData.residue?.entries?.length > 0;
        const hasWater = loadedFormData.water?.waterEntries?.length > 0;

        hasConsumptionData = hasMaterials && hasElectricity && hasFuels && hasResidue && hasWater;
      }

      // Step 2: General Data - check if meaningful general data exists based on activity type
      const generalData = convertedGeneralData;

      let hasGeneralData = false;

      if (generalData) {
        if (isExtractionCompany) {
          // For extraction companies, check extraction fields
          hasGeneralData =
            generalData.stoneTypeId &&
            generalData.prod_ext &&
            generalData.prod_ext > 0 &&
            generalData.fat_ext &&
            generalData.fat_ext > 0 &&
            generalData.pvp_caro_ext &&
            generalData.pvp_caro_ext > 0 &&
            generalData.pvp_barato_ext &&
            generalData.pvp_barato_ext > 0;
        } else if (isTransformationCompany) {
          // For transformation companies, check transformation fields (no stone type required)
          hasGeneralData =
            generalData.prod_trsf &&
            generalData.prod_trsf > 0 &&
            generalData.fat_trsf &&
            generalData.fat_trsf > 0 &&
            generalData.pvp_caro_trsf &&
            generalData.pvp_caro_trsf > 0 &&
            generalData.pvp_barato_trsf &&
            generalData.pvp_barato_trsf > 0 &&
            generalData.rend &&
            generalData.rend > 0;
        } else if (isMixedCompany) {
          // For mixed companies, check both extraction and transformation fields
          const hasExtractionData =
            generalData.stoneTypeId &&
            generalData.prod_ext &&
            generalData.prod_ext > 0 &&
            generalData.fat_ext &&
            generalData.fat_ext > 0 &&
            generalData.pvp_caro_ext &&
            generalData.pvp_caro_ext > 0 &&
            generalData.pvp_barato_ext &&
            generalData.pvp_barato_ext > 0;

          const hasTransformationData =
            generalData.prod_trsf &&
            generalData.prod_trsf > 0 &&
            generalData.fat_trsf &&
            generalData.fat_trsf > 0 &&
            generalData.pvp_caro_trsf &&
            generalData.pvp_caro_trsf > 0 &&
            generalData.pvp_barato_trsf &&
            generalData.pvp_barato_trsf > 0 &&
            generalData.rend &&
            generalData.rend > 0;

          hasGeneralData = hasExtractionData && hasTransformationData;
        }
      }

      // Step 3: Products - check if products exist AND meet validation targets
      const products = loadedFormData.products;
      
      // Filter products based on company activity type
      let relevantProducts = products?.entries || [];
      if (isExtractionCompany) {
        relevantProducts = products?.entries?.filter((p: any) => !p.productFlow || p.productFlow === "extraction") || [];
      } else if (isTransformationCompany) {
        relevantProducts = products?.entries?.filter((p: any) => !p.productFlow || p.productFlow === "transformation") || [];
      }
      // For mixed companies, use all products
      const hasProductData = relevantProducts.length > 0;

      // Use the validation flags from the products payload if available,
      // otherwise perform manual validation for legacy data
      let isProductsValid = false;
      if (hasProductData) {
        if (products.isProductionValid !== undefined && products.isRevenueValid !== undefined) {
          // Use the stored validation flags from ProductStep
          isProductsValid = products.isProductionValid && products.isRevenueValid;
        } else if (generalData) {
          // Use the same validation logic as ProductStep
          let isProductionValid = false;
          let isRevenueValid = false;

          if (isMixedCompany) {
            // For mixed companies, validate each activity type separately
            const extractionProducts = products.entries.filter((p: any) => p.productFlow === "extraction");
            const transformationProducts = products.entries.filter((p: any) => p.productFlow === "transformation");

            // Use the same validation logic as ProductStep
            const extractionValidation = validateProductTotals(extractionProducts, {
              prod_ext: generalData.prod_ext || 0,
              fat_ext: generalData.fat_ext || 0,
            });
            const transformationValidation = validateProductTotals(transformationProducts, {
              prod_ext: generalData.prod_trsf || 0,
              fat_ext: generalData.fat_trsf || 0,
            });

            isProductionValid = extractionValidation.isProductionValid && transformationValidation.isProductionValid;
            isRevenueValid = extractionValidation.isRevenueValid && transformationValidation.isRevenueValid;
          } else {
            // For single activity companies, use the same validation logic as ProductStep
            if (isTransformationCompany) {
              const validation = validateProductTotals(relevantProducts, {
                prod_ext: generalData.prod_trsf || 0,
                fat_ext: generalData.fat_trsf || 0,
              });
              isProductionValid = validation.isProductionValid;
              isRevenueValid = validation.isRevenueValid;
            } else if (isExtractionCompany) {
              const validation = validateProductTotals(relevantProducts, {
                prod_ext: generalData.prod_ext || 0,
                fat_ext: generalData.fat_ext || 0,
              });
              isProductionValid = validation.isProductionValid;
              isRevenueValid = validation.isRevenueValid;
            }
          }

          isProductsValid = isProductionValid && isRevenueValid;
        }
      }

      // Step 4: Revision & Submission - completed if status is "submitted"
      const isSubmissionCompleted = status === "submitted";

      // Update steps with completion status
      setSteps((prevSteps: StepType[]) => [
        { ...prevSteps[0], isCompleted: yearCompleted },
        { ...prevSteps[1], isCompleted: hasConsumptionData },
        { ...prevSteps[2], isCompleted: Boolean(hasGeneralData) },
        { ...prevSteps[3], isCompleted: isProductsValid },
        { ...prevSteps[4], isCompleted: isSubmissionCompleted },
      ]);
    },
    [company?.activityType]
  );

  /**
   * Handle form data changes
   */
  const handleDataChange = useCallback(
    (category: string, data: any) => {
      setFormData((prev) => ({ ...prev, [category]: data }));
    },
    []
  );

  /**
   * Helper function to ensure material entries have all required fields
   */
  const mapMaterialEntries = (entries: any[] = []) => {
    if (!Array.isArray(entries)) {
      return [];
    }

    return entries.map((entry) => ({
      ...entry,
      materialType: entry.materialType || "",
      materialName: entry.materialName || entry.materialType || "",
      setup_item_id: entry.setup_item_id || null,
      setup_item_name: entry.setup_item_name || entry.materialName || entry.materialType || "",
    }));
  };

  /**
   * Clean up distribution data for deleted products
   */
  const cleanupDistributionData = async (deletedProductIds: string[]) => {
    if (!deletedProductIds || deletedProductIds.length === 0) return;

    try {
      const { distributionService } = await import("@/services/distribution/distributionService");
      
      // Delete distribution data for each deleted product
      const deletePromises = deletedProductIds.map(productId => 
        distributionService.deleteDistributionByProduct(productId).catch(error => {
          console.warn(`Failed to delete distribution data for product ${productId}:`, error);
        })
      );
      
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error removing distribution data:", error);
    }
  };

  /**
   * Save as draft
   */
  const handleSaveDraft = async () => {
    setIsSavingDraft(true);
    try {
      // Map the form data to ensure all required fields are present
      let dataToSave = { ...formData };

      if (formData.products?.deletedProductIds?.length > 0) {
        await cleanupDistributionData(formData.products.deletedProductIds);

        dataToSave.products = {
          ...dataToSave.products,
          deletedProductIds: []
        };
      }

      // Ensure generalData section exists and has activityType
      if (!dataToSave.generalData) dataToSave.generalData = {};
      if (!dataToSave.generalData.activityType && company?.activityType) {
        dataToSave.generalData.activityType = company.activityType;
      }

      // Handle material entries based on company type
      const activityType = company?.activityType;
      const isMixed = activityType === "extraction_and_transformation";

      if (isMixed && dataToSave.annualConsumptions) {
        const { extraction = {}, transformation = {} } = dataToSave.annualConsumptions;

        dataToSave = {
          ...dataToSave,
          materials: {
            extraction: {
              ...(Array.isArray(extraction.materials) ? { entries: extraction.materials } : extraction.materials),
              entries: mapMaterialEntries(
                Array.isArray(extraction.materials) ? extraction.materials : extraction.materials?.entries
              ),
            },
            transformation: {
              ...(Array.isArray(transformation.materials)
                ? { entries: transformation.materials }
                : transformation.materials),
              entries: mapMaterialEntries(
                Array.isArray(transformation.materials)
                  ? transformation.materials
                  : transformation.materials?.entries
              ),
            },
          },
          electricity: {
            extraction: extraction.electricity
              ? { ...extraction.electricity, entries: extraction.electricity.entries || [] }
              : { entries: [] },
            transformation: transformation.electricity
              ? { ...transformation.electricity, entries: transformation.electricity.entries || [] }
              : { entries: [] },
          },
          fuels: {
            extraction: extraction.fuels
              ? { ...extraction.fuels, entries: extraction.fuels.entries || [] }
              : { entries: [] },
            transformation: transformation.fuels
              ? { ...transformation.fuels, entries: transformation.fuels.entries || [] }
              : { entries: [] },
          },
          residue: {
            extraction: extraction.residue
              ? { ...extraction.residue, entries: extraction.residue.entries || [] }
              : { entries: [] },
            transformation: transformation.residue
              ? { ...transformation.residue, entries: transformation.residue.entries || [] }
              : { entries: [] },
          },
          water: {
            extraction: extraction.water
              ? { ...extraction.water, waterEntries: extraction.water.waterEntries || [] }
              : { waterEntries: [] },
            transformation: transformation.water
              ? { ...transformation.water, waterEntries: transformation.water.waterEntries || [] }
              : { waterEntries: [] },
          },
        };
      } else {
        // For single-activity companies, structure data in the same format as backend expects
        const activityKey = activityType === "extraction" ? "extraction" : "transformation";

        const materialsSingle = {
          entries: mapMaterialEntries(dataToSave.materials?.entries).map((entry) => ({
            ...entry,
            activity_type: activityKey,
          })),
        };
        const electricitySingle = {
          entries: (dataToSave.electricity?.entries || []).map((e: any) => ({
            ...e,
            activity_type: activityKey,
          })),
        };
        const fuelsSingle = {
          entries: (dataToSave.fuels?.entries || []).map((e: any) => ({
            ...e,
            activity_type: activityKey,
          })),
        };
        const residueSingle = {
          entries: (dataToSave.residue?.entries || []).map((e: any) => ({
            ...e,
            activity_type: activityKey,
          })),
        };
        const waterSingle = {
          waterEntries: (dataToSave.water?.waterEntries || []).map((e: any) => ({
            ...e,
            activity_type: activityKey,
          })),
        };

        dataToSave = {
          ...dataToSave,
          materials: { [activityKey]: materialsSingle } as any,
          electricity: { [activityKey]: electricitySingle } as any,
          fuels: { [activityKey]: fuelsSingle } as any,
          residue: { [activityKey]: residueSingle } as any,
          water: { [activityKey]: waterSingle } as any,
        };

        dataToSave.annualConsumptions = {
          [activityKey]: {
            materials: materialsSingle,
            electricity: electricitySingle,
            fuels: fuelsSingle,
            residue: residueSingle,
            water: waterSingle,
          },
        } as any;
      }

      await saveFootprintsAsDraft(selectedYear, claims?.companyId || "", dataToSave);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      toast({
        title: t("footprints.toasts.draftSaved"),
      });
    } catch (error) {
      console.error("Error saving draft:", error);
      toast({
        title: t("footprints.toasts.draftSaveError"),
        variant: "destructive",
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  /**
   * Handle year selection and data loading
   */
  const handleYearChange = useCallback(
    async (year: string) => {
      setSelectedYear(year);
      if (year && claims?.companyId) {
        await loadExistingData(year, claims.companyId);
      }
    },
    [claims?.companyId, loadExistingData]
  );

  /**
   * Handle step changes
   */
  const handleStepChange = useCallback(
    (stepIndex: number, subStep?: number) => {
      // Ensure step is valid and enabled before changing
      if (stepIndex >= 0 && stepIndex < steps.length) {
        const targetStep = steps[stepIndex];
        if (targetStep && targetStep.isEnabled !== false) {
          setCurrentStep(stepIndex);
          // Handle substep navigation for Annual Consumptions (step 1)
          if (stepIndex === 1 && subStep !== undefined) {
            setSubStepIndex(subStep);
          } else if (stepIndex !== 1) {
            // Reset sub-step when switching to other main steps
            setSubStepIndex(0);
          }
        }
      }
    },
    [steps]
  );

  // Step completion handlers
  const updateStepCompletion = useCallback((stepIndex: number, isCompleted: boolean) => {
    setSteps((prevSteps: StepType[]) =>
      prevSteps.map((step, index) => (index === stepIndex ? { ...step, isCompleted } : step))
    );
  }, []);

  const handleConsumptionsComplete = useCallback(() => updateStepCompletion(1, true), [updateStepCompletion]);
  const handleConsumptionsIncomplete = useCallback(() => updateStepCompletion(1, false), [updateStepCompletion]);
  const handleGeneralDataComplete = useCallback(() => updateStepCompletion(2, true), [updateStepCompletion]);
  const handleGeneralDataIncomplete = useCallback(() => updateStepCompletion(2, false), [updateStepCompletion]);
  const handleProductComplete = useCallback(() => updateStepCompletion(3, true), [updateStepCompletion]);
  const handleProductIncomplete = useCallback(() => updateStepCompletion(3, false), [updateStepCompletion]);
  const handleRevisionSubmissionComplete = useCallback(() => {
    updateStepCompletion(4, true);
    setOriginalStatus("submitted");
    // Reset unsaved changes state after successful submission
    setHasUnsavedChanges(false);
    setOriginalFormData(formData);
  }, [updateStepCompletion, formData]);

  const handleMainStepperComplete = useCallback(() => {
    toast({
      title: t("success"),
      description: t("footprints.dataSubmittedSuccessfully"),
    });
  }, [toast, t]);

  // Deep comparison utility to detect actual data changes
  const deepEqual = useCallback((obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return obj1 === obj2;
    if (typeof obj1 !== typeof obj2) return false;
    if (typeof obj1 !== "object") return obj1 === obj2;
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
  }, []);

  // Check for unsaved changes
  useEffect(() => {
    const currentDataWithoutMeta = { ...formData };
    const originalDataWithoutMeta = { ...originalFormData };

    // Exclude metadata from comparison
    delete currentDataWithoutMeta._metadata;
    delete originalDataWithoutMeta._metadata;
    delete currentDataWithoutMeta._skippedSteps;
    delete originalDataWithoutMeta._skippedSteps;

    const hasChanges = !deepEqual(currentDataWithoutMeta, originalDataWithoutMeta);
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalFormData, deepEqual]);

  // Keep stepper completion in sync with formData
  useEffect(() => {
    if (!isLoadingData) {
      evaluateStepCompletionStates(formData, _originalStatus);
    }
  }, [formData, _originalStatus, isLoadingData, evaluateStepCompletionStates]);

  // Helper to ensure all products have a valid productFlow
  function getTransformedProducts(
    products: ProductPayload | undefined,
    company: Company | null | undefined
  ): ProductPayload | undefined {
    if (!products || !Array.isArray(products.entries)) {
      return products;
    }
    
    const activityType = company?.activityType;
    const transformed = {
      ...products,
      entries: products.entries.map((entry: ProductEntry) => {
        if (entry.productFlow) return entry;
        // Infer productFlow if missing
        if (activityType === "extraction") return { ...entry, productFlow: "extraction" as const };
        if (activityType === "transformation") return { ...entry, productFlow: "transformation" as const };
        // For mixed, fallback to extraction if unsure
        return { ...entry, productFlow: "extraction" as const };
      }),
    };

    return transformed;
  }

  // The content for the steps
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <YearSelectionStep
              selectedYear={selectedYear}
              onYearChange={handleYearChange}
              loading={isLoadingData}
              isEditMode={isEditMode}
              isLoadingData={isLoadingData}
              loadedFormData={formData}
              company={company}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              hasUnsavedChanges={hasUnsavedChanges}
            />

            {loadingError && (
              <div className="mt-4">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{loadingError}</AlertDescription>
                </Alert>
              </div>
            )}
          </div>
        );
      case 1:
        return (
          selectedYear && (
            <AnnualConsumptionsStep
              year={selectedYear}
              existingData={formData}
              onDataChange={handleDataChange}
              formData={formData}
              currentSubStep={subStepIndex}
              onSubStepChange={setSubStepIndex}
              onComplete={handleConsumptionsComplete}
              onIncomplete={handleConsumptionsIncomplete}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              hasUnsavedChanges={hasUnsavedChanges}
              lastSaved={lastSaved}
              onNavigateToNextMainStep={() => handleStepChange(2)}
              companyType={company?.activityType}
            />
          )
        );
      case 2:
        return (
          selectedYear && (
            <GeneralDataStep
              year={selectedYear}
              existingData={formData.generalData}
              onDataChange={(data) => handleDataChange("generalData", data)}
              onComplete={handleGeneralDataComplete}
              onIncomplete={handleGeneralDataIncomplete}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              hasUnsavedChanges={hasUnsavedChanges}
              lastSaved={lastSaved}
            />
          )
        );
      case 3:
        return (
          selectedYear && (
            <ProductStep
              year={selectedYear}
              existingData={getTransformedProducts(formData.products, company)}
              onDataChange={(data) => handleDataChange("products", data)}
              onComplete={handleProductComplete}
              onIncomplete={handleProductIncomplete}
              calculationInputs={calculationInputs}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              hasUnsavedChanges={hasUnsavedChanges}
              lastSaved={lastSaved}
              formData={formData}
            />
          )
        );
      case 4:
        return (
          selectedYear && (
            <RevisionSubmissionStep
              year={selectedYear}
              formData={formData}
              company={company}
              onSubmissionSuccess={handleRevisionSubmissionComplete}
              onSaveDraft={handleSaveDraft}
              isSavingDraft={isSavingDraft}
              hasUnsavedChanges={hasUnsavedChanges}
              lastSaved={lastSaved}
              originalStatus={_originalStatus}
              onNavigateToStep={handleStepChange}
            />
          )
        );
      default:
        return <div>Invalid step</div>;
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="w-full flex-1 px-4 md:px-8 py-8">
        <div className="w-full max-w-6xl mx-auto">
          <Stepper
            steps={steps}
            activeStep={currentStep}
            onStepChange={handleStepChange}
            onComplete={handleMainStepperComplete}
          >

            <div className="stepper-content my-6">{renderStepContent()}</div>

            {/* Hide StepperControls on the final step since RevisionSubmissionStep has its own submit button */}
            {currentStep < 4 && (() => {
              // Dynamic next button label based on current step
              let nextLabel = t("common.next");

              if (currentStep === 0 && steps[1]) {
                nextLabel = `${t("common.advanceTo")} ${steps[1].title}`;
              } else if (currentStep === 1 && steps[2]) {
                nextLabel = `${t("common.advanceTo")} ${steps[2].title}`;
              } else if (currentStep === 2 && steps[3]) {
                nextLabel = `${t("common.advanceTo")} ${steps[3].title}`;
              } else if (currentStep === 3 && steps[4]) {
                nextLabel = `${t("common.advanceTo")} ${steps[4].title}`;
              }

              return (
                <StepperControls
                  backLabel={t("common.back")}
                  nextLabel={nextLabel}
                  completeLabel={t("common.save")}
                  skipLabel={t("common.markDone")}
                  hideBackOnFirst={true}
                />
              );
            })()}
          </Stepper>
        </div>
      </div>
    </div>
  );
};
